/**
 * Role Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useRoleStore } from '../../store/role.store.js';
import { Logger } from '@/lib/app-log.js';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { resolveRoleListFilters } from '@/lib/role-list-filters.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';

export class RoleIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchRoles(params = {}) {
        const store = useRoleStore.getState();
        const {
            columnFilters,
            applySystemRoleVisibility = false,
            ...requestParams
        } = params;
        const sourceFilters = columnFilters ?? (applySystemRoleVisibility ? store.columnFilters : undefined);
        const requestFilters = applySystemRoleVisibility
            ? resolveRoleListFilters(sourceFilters)
            : sourceFilters;
        const queryParams = {
            ...requestParams,
            ...(requestFilters ? mapColumnFiltersForQuery(requestFilters) : {}),
        };

        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(queryParams);
            const { data, pagination, uniqueValues } = response;

            if (store.setRoles) store.setRoles(data || []);

            if (pagination && store.setPagination) {
                store.setPagination({
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    totalPages: pagination.totalPages
                });
            }

            if (uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /** Lookup list without mutating the role admin store or toasting. */
    async queryAll(params = {}) {
        return this.service.getAll(params);
    }

    /** Assignment pickers need every role, including system roles, without touching the admin list. */
    async fetchAssignableRoles(params = {}) {
        try {
            const response = await this.service.getAll({ page: 1, pageSize: 200, ...params });
            return { success: true, data: response.data || [] };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Fetch roles for dropdown
     */
    async fetchRolesForDropdown() {
        const store = useRoleStore.getState();
        try {
            const result = await fetchDropdownOptionsFromReference('role', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
            const data = result.data || [];
            const dropdownData = data.map((role) => ({
                value: role.value ?? role._id ?? role.id,
                label: role.label ?? role.name ?? role.title ?? role.roleName ?? 'Unnamed Role',
            }));
            if (store.setRolesForDropdown) {
                store.setRolesForDropdown(dropdownData);
            }
        } catch (error) {
            Logger.error('Failed to fetch role dropdown', { error: error?.message });
        }
    }

    /**
     * Get by ID
     * @param {string} id
     * @param {{ silent?: boolean }} [opts] — skip toast (e.g. form enrichment after tenant switch)
     */
    async fetchRoleById(id, opts = {}) {
        const store = useRoleStore.getState();
        const silent = Boolean(opts.silent);
        if (!silent && store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.getById(id);

            if (store.setSelectedRole) {
                store.setSelectedRole(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!silent && store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createRole(data) {
        const store = useRoleStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('integrationToasts:roleCreated', { defaultValue: 'Role created' }));

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateRole(id, data) {
        const store = useRoleStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:roleUpdated', { defaultValue: 'Role updated' }));

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteRole(id) {
        const store = useRoleStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:roleDeleted', { defaultValue: 'Role deleted' }));

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Bulk delete items
     */
    async bulkDeleteRoles(ids) {
        const store = useRoleStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:selectedRolesDeleted', { defaultValue: 'Selected roles deleted' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Clone role
     */
    async cloneRole(id, data) {
        const store = useRoleStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.createClone(id, data);
            toast.success(i18next.t('integrationToasts:roleCopied', { defaultValue: 'Role copied' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Assign multiple tenant users to a role (API merges roles on membership).
     */
    async assignUsersToRole(roleId, userIds) {
        try {
            const result = await this.service.assignUsersToRole(roleId, userIds);
            const { updated, skippedNoMembership, alreadyHad } = result;
            const parts = [];
            if (updated > 0) {
                parts.push(i18next.t('integrationToasts:roleUsersAdded', {
                    count: updated,
                    defaultValue: '{{count}} users added to role',
                }));
            }
            if (alreadyHad?.length) {
                parts.push(i18next.t('integrationToasts:roleUsersAlreadyHad', {
                    count: alreadyHad.length,
                    defaultValue: '{{count}} users already had this role',
                }));
            }
            if (skippedNoMembership?.length) {
                parts.push(i18next.t('integrationToasts:roleUsersSkippedNoMembership', {
                    count: skippedNoMembership.length,
                    defaultValue: '{{count}} users skipped (no tenant membership)',
                }));
            }
            if (parts.length > 0) {
                toast.success(parts.join(' · '));
            } else {
                toast.message(i18next.t('integrationToasts:roleUsersNoOp', {
                    defaultValue: 'No changes for selected users',
                }));
            }
            return {
                success: true,
                updated,
                skippedNoMembership,
                alreadyHad
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Replace the units (units) linked to this role.
     */
    async setRoleUnits(roleId, unitIds) {
        try {
            const result = await this.service.setUnits(roleId, unitIds);
            toast.success(i18next.t('integrationToasts:unitsUpdated', { defaultValue: 'Units updated' }));
            return { success: true, data: result.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Replace the per-unit user assignments for this role.
     * Surfaces 409 conflicts so the caller can ask the user for an override.
     */
    async setRoleUnitAssignments(roleId, assignments, options = {}) {
        try {
            const result = await this.service.setUnitAssignments(roleId, assignments, options);
            toast.success(i18next.t('integrationToasts:unitRoleAssignmentsUpdated', { defaultValue: 'Unit-role assignments updated' }));
            return { success: true, data: result.data };
        } catch (error) {
            if (error.statusCode === 409 || error.status === 409) {
                return {
                    success: false,
                    conflict: true,
                    conflicts: error.data?.conflicts || error.details?.conflicts || [],
                    error: this._getErrorMessage(error),
                };
            }
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * List the unit-role assignments for a given unit.
     */
    async listUnitRolesForUnit(unitId) {
        try {
            const result = await this.service.listUnitRolesForUnit(unitId);
            return { success: true, data: result.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
